import { createFileRoute } from "@tanstack/react-router";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Reveal } from "@/components/Reveal";
import { SPECIALTIES } from "@/data/heallexa";
import { specialtyOverviewPath } from "@/lib/seo-slugs";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/specialties/")({
 head: () => {
 const title = "Browse Doctors by Specialty | Heallexa";
 const description =
 "Browse all " + SPECIALTIES.length + " healthcare specialties on Heallexa. Find verified doctors, dentists, therapists, and specialists across the United States — free, no account required.";
 const url = `${SITE}/specialties`;
 return {
 meta: [
 { title },
 { name: "description", content: description },
 { property: "og:title", content: title },
 { property: "og:description", content: description },
 { property: "og:url", content: url },
 { property: "og:type", content: "website" },
 { name: "twitter:title", content: title },
 { name: "twitter:description", content: description },
 ],
 links: [{ rel: "canonical", href: url }],
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "CollectionPage",
 name: title,
 description,
 url,
 isPartOf: { "@id": `${SITE}/#website` },
 about: { "@type": "Thing", name: "Medical Specialties" },
 breadcrumb: {
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "Home", item: SITE },
 { "@type": "ListItem", position: 2, name: "Specialties", item: url },
 ],
 },
 }),
 },
 ],
 };
 },
 component: SpecialtiesIndex,
});

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

function SpecialtiesIndex() {
 const byLetter = LETTERS.map((l) => ({
 letter: l,
 items: SPECIALTIES.filter((s) => s[0].toUpperCase() === l),
 })).filter((g) => g.items.length > 0);

 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
 <Reveal from="up">
 <header className="mb-10">
 <p className="font-display text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">Heallexa Directory</p>
 <h1 className="font-display text-[clamp(1.8rem,3vw,2.5rem)] font-semibold tracking-[-0.03em] leading-[1.15] text-foreground">
 Browse Doctors by Specialty
 </h1>
 <p className="mt-3 text-[14px] text-muted-foreground max-w-[640px] leading-relaxed">
 Heallexa covers <strong className="text-foreground font-medium">{SPECIALTIES.length} healthcare specialties</strong> across
 6.3 million verified U.S. providers — from primary care, pediatrics, and dentistry to
 cardiology, psychiatry, and physical therapy. Select a specialty below to find verified
 providers near you, filtered by location, insurance, and availability.
 </p>
 </header>
 </Reveal>

 <nav aria-label="A Z" className="mb-12 flex flex-wrap gap-2">
 {byLetter.map((g) => (
 <a
 key={g.letter}
 href={`/specialties/${g.letter.toLowerCase()}`}
 className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-border bg-background text-[13px] font-medium text-foreground hover:bg-muted transition-colors"
 >
 {g.letter}
 </a>
 ))}
 </nav>

 <div className="space-y-12">
 {byLetter.map((g, idx) => (
 <Reveal key={g.letter} as="section" from="up" delay={Math.min(idx, 8) * 70}>
 <section id={g.letter}>
 <h2 className="font-display text-[20px] font-semibold tracking-[-0.02em] text-foreground pb-3 mb-4 border-b border-border">
 {g.letter}
 </h2>
 <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-6">
 {g.items.map((s) => (
 <li key={s}>
 <a
 href={specialtyOverviewPath(s)}
 className="text-[14px] text-muted-foreground hover:text-primary transition-colors"
 >
 {s}
 </a>
 </li>
 ))}
 </ul>
 </section>
 </Reveal>
 ))}
 </div>
 </main>
 <FooterSection />
 </div>
 );
}
